// RTK Query slice for workspace connections (CLE-515) — wire contract for
// cleanse-api `cleanse_api/api/integrations/workspace.py`
// (prefix /api/v1/integrations/workspace). Google Sheets and Google Docs are
// each member's own, separate connections (`google_sheets`, `google_docs`), so
// a member can make either or both; every Google route acts on the signed-in
// member's own. Fireflies (`fireflies`), Fathom (`fathom`) and Gong (`gong`, CLE-508) sync meetings into
// the knowledge base; Airtable, Notion and 6sense for Clu (`sixsense_mcp`,
// each member's own 6sense MCP grant) reuse the same `/{provider}` routes.
//
// The OAuth flow is browser-driven: `connectWorkspace` returns an
// `authorizeUrl` the card navigates to; the provider redirects back to the
// portal's /integrations/{google,fireflies,fathom,gong}/callback page, which calls
// `completeWorkspace` and then lands on /integrations?{vendor}_connected=…
// (or ?{vendor}_error=...). Every route 404s while the provider's flag is off.

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

export type WorkspaceProvider =
  | 'google_sheets'
  | 'google_docs'
  | 'google_calendar'
  | 'fireflies'
  | 'fathom'
  | 'gong'
  | 'notion'
  | 'airtable'
  | 'sixsense_mcp';

export interface WorkspaceStatusResponse {
  provider: WorkspaceProvider;
  connected: boolean;
  accountLabel?: string | null;
  connectedByUserId?: string | null;
  connectedAt?: string | null;
  reconnectRequired: boolean;
  configured: boolean;
  canManage: boolean;
}

export interface WorkspaceCalendarMember {
  googleUserId: string;
  accountLabel?: string | null;
}

export interface WorkspaceSheetTab {
  sheetId: number;
  sheetName: string;
}

// One tab as CSV; `tabs` lists every grid tab of the spreadsheet.
export interface WorkspaceSheetCsv {
  csv: string;
  spreadsheetName?: string;
  sheetId?: number | null;
  sheetName?: string;
  tabs?: WorkspaceSheetTab[];
}

/** A sheet for an Export step, and whose Drive holds it: the step writes
 * through that member's connection, the only grant that can reach the file. */
export interface WorkspaceSheet {
  spreadsheetId: string;
  spreadsheetUrl: string;
  spreadsheetName: string;
  sheetName: string;
  googleUserId?: string | null;
  accountLabel?: string | null;
}

interface WorkspaceArgs {
  provider: WorkspaceProvider;
  organizationId: string;
}

const headers = (organizationId: string) => ({
  'X-Cleanlist-Expected-Org': organizationId,
});

const statusTag = ({ provider, organizationId }: WorkspaceArgs) => ({
  type: 'WorkspaceConnection' as const,
  id: `${provider}:${organizationId}`,
});

export const workspaceConnectionsApi = createApi({
  reducerPath: 'workspaceConnectionsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['WorkspaceConnection'],
  endpoints: (builder) => ({
    getWorkspaceCalendarMembers: builder.query<
      WorkspaceCalendarMember[],
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: '/integrations/workspace/google_calendar/members',
        headers: headers(organizationId),
      }),
      providesTags: (_result, _error, { organizationId }) => [
        statusTag({ provider: 'google_calendar', organizationId }),
      ],
    }),
    getWorkspaceStatus: builder.query<WorkspaceStatusResponse, WorkspaceArgs>({
      query: ({ provider, organizationId }) => ({
        url: `/integrations/workspace/${provider}/status`,
        headers: headers(organizationId),
      }),
      providesTags: (_result, _error, args) => [statusTag(args)],
    }),
    connectWorkspace: builder.mutation<{ authorizeUrl: string }, WorkspaceArgs>(
      {
        query: ({ provider, organizationId }) => ({
          url: `/integrations/workspace/${provider}/connect`,
          method: 'POST',
          headers: headers(organizationId),
        }),
      }
    ),
    // Finishes the OAuth flow from the portal's callback page. Bearer-authed,
    // so the backend can require that the signed-in user and org are the ones
    // who started it.
    completeWorkspace: builder.mutation<
      { connected: boolean; accountLabel?: string | null },
      WorkspaceArgs & { code: string; state: string }
    >({
      query: ({ provider, organizationId, code, state }) => ({
        url: `/integrations/workspace/${provider}/complete`,
        method: 'POST',
        headers: headers(organizationId),
        body: { code, state },
      }),
      invalidatesTags: (_result, _error, args) => [statusTag(args)],
    }),
    // Gong with an access key + secret the org's Gong admin made, for Gong
    // companies our Gong app can't be installed in yet (it isn't public).
    connectGongAccessKey: builder.mutation<
      { connected: boolean; accountLabel?: string | null },
      {
        organizationId: string;
        accessKey: string;
        accessKeySecret: string;
        domain: string;
      }
    >({
      query: ({ organizationId, ...body }) => ({
        url: '/integrations/workspace/gong/access-key',
        method: 'POST',
        headers: headers(organizationId),
        body,
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        statusTag({ provider: 'gong', organizationId }),
      ],
    }),
    // An empty Sheet for a play's Export step (drive.file can only write to
    // sheets the app created, so the builder creates one instead of listing).
    createWorkspaceSheet: builder.mutation<
      WorkspaceSheet,
      WorkspaceArgs & { title: string }
    >({
      query: ({ provider, organizationId, title }) => ({
        url: `/integrations/workspace/${provider}/sheets`,
        method: 'POST',
        headers: headers(organizationId),
        body: { title },
      }),
    }),
    exportListToWorkspaceSheet: builder.mutation<
      { spreadsheetId: string; spreadsheetUrl: string; rowCount: number },
      WorkspaceArgs & { listId: string }
    >({
      query: ({ provider, organizationId, listId }) => ({
        url: `/integrations/workspace/${provider}/lists/${encodeURIComponent(listId)}/export`,
        method: 'POST',
        headers: headers(organizationId),
      }),
    }),
    // Google Picker: the member's own Google token, the browser API key and
    // the Cloud project number (409 not_connected until they connect).
    getPickerToken: builder.mutation<
      { accessToken: string; apiKey: string; appId: string },
      WorkspaceArgs
    >({
      query: ({ provider, organizationId }) => ({
        url: `/integrations/workspace/${provider}/picker-token`,
        method: 'POST',
        headers: headers(organizationId),
      }),
    }),
    // A picked Sheet's name, link and first tab (an Export step's target).
    getWorkspaceSheet: builder.query<
      WorkspaceSheet,
      WorkspaceArgs & { spreadsheetId: string }
    >({
      query: ({ provider, organizationId, spreadsheetId }) => ({
        url: `/integrations/workspace/${provider}/sheets/${encodeURIComponent(spreadsheetId)}`,
        headers: headers(organizationId),
      }),
    }),
    // A picked Sheet's tab as CSV (`gid` from a pasted link or the tab select,
    // else the first tab) plus its grid tabs, for the list import wizards and
    // Clu's chat import.
    getWorkspaceSheetCsv: builder.query<
      WorkspaceSheetCsv,
      WorkspaceArgs & { spreadsheetId: string; gid?: number | null }
    >({
      query: ({ provider, organizationId, spreadsheetId, gid }) => ({
        url: `/integrations/workspace/${provider}/sheets/${encodeURIComponent(spreadsheetId)}/csv`,
        params: gid != null ? { gid } : undefined,
        headers: headers(organizationId),
      }),
      keepUnusedDataFor: 0,
    }),
    disconnectWorkspace: builder.mutation<
      { disconnected: boolean },
      WorkspaceArgs
    >({
      query: ({ provider, organizationId }) => ({
        url: `/integrations/workspace/${provider}`,
        method: 'DELETE',
        headers: headers(organizationId),
      }),
      invalidatesTags: (_result, _error, args) => [statusTag(args)],
    }),
  }),
});

export const {
  useGetWorkspaceStatusQuery,
  useGetWorkspaceCalendarMembersQuery,
  useConnectWorkspaceMutation,
  useCompleteWorkspaceMutation,
  useConnectGongAccessKeyMutation,
  useCreateWorkspaceSheetMutation,
  useExportListToWorkspaceSheetMutation,
  useGetPickerTokenMutation,
  useLazyGetWorkspaceSheetQuery,
  useLazyGetWorkspaceSheetCsvQuery,
  useDisconnectWorkspaceMutation,
} = workspaceConnectionsApi;
